import React from "react";
import { useState } from "react";
import type { ScreenProps } from "./types";
import { ScreenContainer } from "../../components";
import { ProgressBar, ActivityIndicator } from "react-native-paper";
import { View, Text, Platform } from "react-native";
import ActionsContainer from "../../components/ActionsContainer";
import { ScrollableContainer } from "../../components";
import Button from "../../components/Button";
import { colors } from "../../../theme";
import { gql, useQuery } from "@apollo/client";
import ConfirmationModal from "./ConfirmationModal";
import PaymentChoiceModal from "./PaymentChoiceModal";
import { useFeatureFlags } from "../../hooks/useFlagr";

export const ACCOUNT_QUERY = gql`
  query currentMemberAccountQuery {
    currentPerson {
      id
      primarySectionAffiliation {
        id
        section {
          id
          name
        }
      }
      employments {
        id
        title
        isPrimary
        facility {
          id
          name
          addresses {
            address1
            address2
            city
            state
            postalCode
          }
          section {
            id
            name
          }
        }
      }
    }
  }
`;
export interface Address {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
}

interface Section {
  name: string;
}

export interface Employment {
  facility: {
    name: string;
    addresses: [Address];
    section: Section;
  };
  title?: string;
  isPrimary: boolean;
}

export type Query = {
  currentPerson: {
    employments: [Employment];
    primarySectionAffiliation: {
      section: Section;
    };
  };
};
function DuesReviewScreen({ navigation, route }: ScreenProps<"DuesReview" | "DuesProxyReview">) {
  const { data, loading } = useQuery<Query>(ACCOUNT_QUERY);
  const [showModal, setShowModal] = useState(false);
  const [showPaymentChoiceModal, setShowPaymentChoiceModal] = useState(false);
  const assignProxy = route.name === "DuesProxyReview";
  const { showProxyModal } = route.params || {};
  const flags = useFeatureFlags();

  if (loading || !data) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }
  const { employments, primarySectionAffiliation } = data.currentPerson;

  const primaryEmployment = employments.filter((employment) => employment.isPrimary);
  const secondaryEmployments = employments.filter((employment) => !employment.isPrimary);

  const sortedEmployments = [...primaryEmployment, ...secondaryEmployments];
  const multipleSections = sortedEmployments.length > 1;

  function onContinue() {
    if (assignProxy) {
      navigation.navigate("AssignProxy", {
        ...(flags["dues-order-lookup"] && { orderId: route.params?.orderId }),
      });
    } else {
      if (showProxyModal) {
        setShowPaymentChoiceModal(true);
      } else {
        navigation.navigate("DuesSummary", {
          ...(flags["dues-order-lookup"] && { orderId: route.params?.orderId }),
        });
      }
    }
  }

  const onIncorrectData = () => {
    setShowModal(true);
  };

  return (
    <ScreenContainer navigationTitle="Verify Information">
      {Platform.OS !== "web" && <ProgressBar progress={0.5} color={colors.primary.light} />}
      <ScrollableContainer>
        <View className="px-7 py-6">
          <Text className="text-small-normal font-400 text-ink-dark">
            Accurate employment information is required for proper dues calculations, please review and verify that all
            information is correct.
          </Text>
          {multipleSections && (
            <Text className="mt-2 text-small-normal font-400 text-ink-dark">
              <Text className="font-700">Note:</Text> You are currently employed in two sections, please review and verify this
              is correct.
            </Text>
          )}
        </View>
        <View className="web:pb-8">
          {sortedEmployments.length === 0 ? (
            <EmploymentItem
              header={employmentHeaderDisplay()}
              sectionName={primarySectionAffiliation?.section?.name}
              jobTitleLabel="Employment Status"
              employmentTitle="Unemployed / Retired"
            />
          ) : (
            sortedEmployments.map((employment, i) => {
              const address = employment.facility.addresses[0];
              return (
                <EmploymentItem
                  key={i}
                  header={employmentHeaderDisplay(employment, multipleSections)}
                  sectionName={
                    (employment.isPrimary && primarySectionAffiliation?.section?.name) || employment.facility.section?.name
                  }
                  facilityName={employment.facility.name}
                  address={address}
                  employmentTitle={employment.title}
                />
              );
            })
          )}
        </View>
      </ScrollableContainer>
      <ActionsContainer horizontalStyle="webLeft">
        <Button mode="outlined" onPress={onIncorrectData}>
          Incorrect. Updates Needed
        </Button>
        <Button mode="contained" icon="check" iconPosition="right" onPress={onContinue} className="mt-2 sm:mt-0 sm:mr-2">
          Information is Correct
        </Button>
      </ActionsContainer>
      <ConfirmationModal
        onDismiss={() => {
          setShowModal(false);
        }}
        visible={showModal}
      />
      <PaymentChoiceModal
        onDismiss={() => {
          setShowPaymentChoiceModal(false);
        }}
        visible={showPaymentChoiceModal}
      />
    </ScreenContainer>
  );
}

function employmentHeaderDisplay(employment?: Employment, multipleSections?: boolean) {
  if (employment && multipleSections) {
    return `${employment.isPrimary ? "Primary" : "Secondary"} Employment ${multipleSections ? "& Section" : ""}`;
  } else {
    return "Employment & Section";
  }
}

const EmploymentItem = ({
  header,
  sectionName,
  facilityName,
  address,
  employmentTitle,
  jobTitleLabel = "Job Title",
}: {
  header: string;
  sectionName?: string;
  facilityName?: string;
  address?: Address;
  employmentTitle?: string;
  jobTitleLabel?: string;
}) => {
  return (
    <View>
      <View className="bg-sky-lighter h-1.5 md:h-0.5" />
      <View className="px-7 py-6">
        <Text className="font-700 text-large-tight">{header}</Text>

        <Text className="mt-6 font-700 text-regular-tight">Section</Text>
        <Text className="mt-2 text-regular-tight font-400 text-ink-dark">{sectionName ?? "None"}</Text>

        {!!facilityName && (
          <>
            <Text className="mt-6 font-700 text-regular-tight">Facility</Text>
            <Text className="mt-2 text-regular-tight font-400 text-ink-dark">{facilityName}</Text>
          </>
        )}
        {address && (
          <>
            <Text className="text-regular-tight font-400 text-ink-dark">
              {address.address2} {address.address1}
            </Text>
            <Text className="text-regular-tight font-400 text-ink-dark">
              {address.city}, {address.state} {address.postalCode}
            </Text>
          </>
        )}

        <Text className="mt-6 font-700 text-regular-tight">{jobTitleLabel}</Text>
        <Text className="mt-2 text-regular-tight font-400 text-ink-dark">{employmentTitle ?? "None"}</Text>
      </View>
    </View>
  );
};

export default DuesReviewScreen;
